import { ReactNode, useEffect, useState } from "react";

import {
  Autocomplete,
  AutocompletePaperSlotPropsOverrides,
  AutocompleteProps,
  AutocompleteValue,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  PaperProps,
  Typography,
} from "@mui/material";

import { debounce, uniqBy } from "lodash";
import { DefaultTextField } from "src/components/Forms/DefaultInputs";

import { Address } from "@models";
import { AddressType } from "@models/Address";
import { cn } from "@utils/tailwind";

import { AddressNameLabel } from "./AddressNameLabel";
import { useAddressesQuery } from "./addressQueries";
import { formatAddress } from "./helpers";

const insureArray = <T,>(value: T | T[] | null | undefined) =>
  Array.isArray(value)
    ? value
    : value === null || value === undefined
      ? []
      : [value];

const getLabel = (address: Address) => {
  return address.type === "custom"
    ? `c${address.id} ${address.name}`
    : `${address.abn ?? ""} ${address.name}`;
};

const filterAddresses = (options: Address[], inputValue: string) => {
  const lowerCasedInputValue = inputValue.toLowerCase();

  // This should match the logic for the search filter in address_controller in the backend.
  return options.filter((option) => {
    const nameMatches = (option.name ?? "")
      .toLowerCase()
      .includes(lowerCasedInputValue);
    const abnMatches = option.abn?.toLowerCase().includes(lowerCasedInputValue);
    const idMatches = `c${option.id}`.includes(lowerCasedInputValue);
    const streetAddress1Matches = option.streetAddress1
      ?.toLowerCase()
      .includes(lowerCasedInputValue);
    const streetAddress2Matches = option.streetAddress2
      ?.toLowerCase()
      .includes(lowerCasedInputValue);
    const cityMatches = option.city
      ?.toLowerCase()
      .includes(lowerCasedInputValue);
    const stateCodeMatches = option.state?.code
      ?.toLowerCase()
      .includes(lowerCasedInputValue);

    return (
      nameMatches ||
      abnMatches ||
      idMatches ||
      streetAddress1Matches ||
      streetAddress2Matches ||
      cityMatches ||
      stateCodeMatches
    );
  });
};

declare module "@mui/material/Autocomplete" {
  interface AutocompletePaperSlotPropsOverrides {
    isLoading?: boolean;
    optionCount?: number;
  }
}

type PaperComponentProps = {
  children?: ReactNode;
} & PaperProps &
  AutocompletePaperSlotPropsOverrides;

/** Module-level so its identity is stable: an inline component would remount the options on every render. */
const PaperComponent = ({
  isLoading = false,
  optionCount = 0,
  children,
  className,
  ...props
}: PaperComponentProps) => (
  <Paper {...props} className={cn("relative", className)}>
    {children}
    {optionCount === 20 && !isLoading && (
      <div className="text-sm text-neutral-500 px-3 py-1">
        Showing top 20 results
      </div>
    )}
    {isLoading && (
      <LinearProgress className="absolute bottom-0 left-0 right-0" />
    )}
  </Paper>
);

type OrderingContextOpts = {
  forDistributor: boolean;
  marketId?: string | null;
  territoryId?: string | null;
  isCouponSweepstakes?: boolean;
};

type AddressSearchProps<Multiple extends boolean> = {
  onChange: (a: AutocompleteValue<Address, Multiple, false, false>) => void;
  reset?: boolean;
  setReset?: (b: boolean) => void;
  autoFocus?: boolean;
  currentTerritory?: boolean;
  filters?: Record<string, any>;
  orderContext?: OrderingContextOpts;
  compliantStateIds?: string[];
} & Omit<
  AutocompleteProps<Address, Multiple, false, false, any>,
  "onChange" | "renderInput" | "options"
>;

const AddressSearch = <Multiple extends boolean = false>({
  onChange,
  reset,
  setReset,
  multiple,
  autoFocus = false,
  orderContext,
  filters,
  compliantStateIds,
  ...props
}: AddressSearchProps<Multiple>) => {
  const emptyValue = (multiple ? [] : null) as AutocompleteValue<
    Address,
    Multiple,
    false,
    false
  >;
  const [address, setAddress] = useState(emptyValue);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: addresses = [], isLoading } = useAddressesQuery({
    search: searchTerm,
    isActive: true,
    ...(orderContext?.forDistributor || orderContext?.isCouponSweepstakes
      ? {
          marketId: orderContext.marketId,
          marketAddressesForTerritoryId: orderContext.territoryId,
          ...(orderContext.isCouponSweepstakes && {
            type: [AddressType.Branch, AddressType.BranchCustom].join(","),
          }),
        }
      : {
          territoryId: orderContext?.territoryId,
          type: [AddressType.Custom, AddressType.Distributor].join(","),
        }),
    ...filters,
  });

  const options = uniqBy(
    [...insureArray(props.value), ...insureArray(addresses)],
    "id"
  );

  const handleChange = debounce((e) => setSearchTerm(e.target.value), 300);

  const isOptionCompliant = (option: Address) => {
    return !compliantStateIds || compliantStateIds.includes(option.state?.id);
  };

  useEffect(() => {
    if (reset) {
      setAddress(emptyValue);
      setReset?.(false);
      setSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, setReset]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      autoHighlight
      multiple={multiple}
      options={options}
      filterOptions={(options, { inputValue }) =>
        filterAddresses(options, inputValue)
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(opt) => opt.name}
      renderOption={(props, option) => {
        const { key: _key, ...optionProps } = props;
        const isNotCompliant =
          !!compliantStateIds && optionProps["aria-disabled"];
        return (
          <li
            {...optionProps}
            key={option.id}
            className={cn(optionProps.className, "group")}
          >
            <Typography noWrap>
              <AddressNameLabel
                isForReyes={option.isForReyes}
                className="whitespace-nowrap text-neutral-800 text-base"
              >
                {getLabel(option)}
              </AddressNameLabel>

              <span className="whitespace-nowrap text-neutral-400 text-sm pl-2 group-hover:text-neutral-700">
                {formatAddress(option)}
              </span>
            </Typography>
            {isNotCompliant && (
              <span className="text-red-500 text-sm text-right ml-auto uppercase font-medium whitespace-nowrap">
                Not Compliant
              </span>
            )}
          </li>
        );
      }}
      value={address}
      onChange={(_, value) => {
        setAddress(value);
        onChange?.(value);
      }}
      componentsProps={{
        popper: {
          style: { width: "auto", maxWidth: "100%" },
          placement: "bottom-start",
        },
      }}
      renderInput={(params) => {
        return (
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <DefaultTextField
              {...params}
              ref={params.InputProps.ref}
              placeholder="Search for an address"
              onChange={handleChange}
              autoFocus={autoFocus}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    {isLoading && <CircularProgress size={20} />}
                  </InputAdornment>
                ),
              }}
            />
          </form>
        );
      }}
      slots={{ paper: PaperComponent }}
      slotProps={{ paper: { isLoading, optionCount: options.length } }}
      {...props}
      getOptionDisabled={(option) =>
        !!props.getOptionDisabled?.(option) || !isOptionCompliant(option)
      }
    />
  );
};

export default AddressSearch;
